/**
 * Account Settings Component
 *
 * Provides account export/import for cross-device portability.
 */
import { useState, useCallback } from 'react';
import type { Identity } from '@amorphous/core';
import {
  encryptBackup,
  decryptBackup,
  restoreKeyPair,
  validateBackupFormat,
} from '@amorphous/core';
import './AccountSettings.css';

interface AccountSettingsProps {
  /** Current identity */
  identity: Identity | null;
  /** Callback to import a new identity */
  onImportIdentity: (privateKey: Uint8Array, publicKey: Uint8Array) => Promise<void>;
  /** Optional callback when settings closed */
  onClose?: () => void;
}

type View = 'main' | 'export' | 'import';

export function AccountSettings({
  identity,
  onImportIdentity,
  onClose,
}: AccountSettingsProps) {
  const [view, setView] = useState<View>('main');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [importCode, setImportCode] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Generate backup code
  const handleExport = useCallback(async () => {
    if (!identity) {
      setError('No identity to export');
      return;
    }

    if (!password) {
      setError('Please enter a password');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const keyPair = identity.getKeyPair();

      const backup = await encryptBackup(keyPair, password);
      setBackupCode(backup);
      setSuccess('Backup code generated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate backup');
    } finally {
      setLoading(false);
    }
  }, [identity, password, confirmPassword]);

  // Copy backup code to clipboard
  const handleCopyBackup = useCallback(async () => {
    if (!backupCode) return;

    try {
      await navigator.clipboard.writeText(backupCode);
      setSuccess('Copied to clipboard');
      setTimeout(() => setSuccess(null), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  }, [backupCode]);

  // Download backup as file
  const handleDownloadBackup = useCallback(() => {
    if (!backupCode) return;

    const blob = new Blob([backupCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aos-backup-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [backupCode]);

  // Import from backup code
  const handleImport = useCallback(async () => {
    if (!importCode.trim()) {
      setError('Please enter or paste a backup code');
      return;
    }

    if (!importPassword) {
      setError('Please enter the backup password');
      return;
    }

    // Validate format first
    const validation = validateBackupFormat(importCode.trim());
    if (!validation.valid) {
      setError(validation.error || 'Invalid backup format');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const payload = await decryptBackup(importCode.trim(), importPassword);
      const keyPair = restoreKeyPair(payload);

      // Import the identity
      await onImportIdentity(keyPair.privateKey, keyPair.publicKey);

      setSuccess('Account restored successfully! Reloading...');
      // Reload to apply new identity
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore backup');
    } finally {
      setLoading(false);
    }
  }, [importCode, importPassword, onImportIdentity]);

  // Handle file drop/select for import
  const handleFileImport = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      setImportCode(text.trim());
    } catch {
      setError('Failed to read file');
    }
  }, []);

  return (
    <div className="account-settings">
      <div className="settings-header">
        <h2>Account Settings</h2>
        {onClose && (
          <button className="close-btn" onClick={onClose}>×</button>
        )}
      </div>

      {/* Main View */}
      {view === 'main' && (
        <div className="settings-main">
          <div className="identity-info">
            <h3>Your Identity</h3>
            {identity ? (
              <>
                <div className="info-row">
                  <span className="info-label">Public Key</span>
                  <code className="info-value">{identity.publicKey}</code>
                </div>
                <div className="info-row">
                  <span className="info-label">Short ID</span>
                  <span className="info-value">{identity.shortId}</span>
                </div>
              </>
            ) : (
              <p className="no-identity">No identity found</p>
            )}
          </div>

          <div className="settings-actions">
            <button
              className="action-btn export"
              onClick={() => setView('export')}
              disabled={!identity}
            >
              Export Account
              <span className="action-desc">Create a backup code to restore on another device</span>
            </button>

            <button
              className="action-btn import"
              onClick={() => setView('import')}
            >
              Import Account
              <span className="action-desc">Restore from a backup code</span>
            </button>
          </div>

          <div className="settings-warning">
            <strong>Warning:</strong> Importing an account will replace your current identity.
            Make sure to export your current account first if you want to keep it.
          </div>
        </div>
      )}

      {/* Export View */}
      {view === 'export' && (
        <div className="settings-export">
          <button className="back-btn" onClick={() => {
            setView('main');
            setPassword('');
            setConfirmPassword('');
            setBackupCode('');
            setError(null);
            setSuccess(null);
          }}>
            ← Back
          </button>

          <h3>Export Account</h3>
          <p>Create an encrypted backup of your account. You'll need the password to restore it.</p>

          {!backupCode ? (
            <div className="export-form">
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a strong password (min 8 chars)"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  disabled={loading}
                />
              </div>

              <button
                className="generate-btn"
                onClick={handleExport}
                disabled={loading || !password || !confirmPassword}
              >
                {loading ? 'Generating...' : 'Generate Backup Code'}
              </button>
            </div>
          ) : (
            <div className="backup-result">
              <div className="backup-code">
                <label>Your Backup Code</label>
                <textarea
                  readOnly
                  value={backupCode}
                  rows={4}
                />
              </div>

              <div className="backup-actions">
                <button onClick={handleCopyBackup}>
                  📋 Copy to Clipboard
                </button>
                <button onClick={handleDownloadBackup}>
                  ⬇️ Download as File
                </button>
              </div>

              <div className="backup-warning">
                <strong>Important:</strong> Save this code securely. Anyone with this code and
                your password can access your account.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import View */}
      {view === 'import' && (
        <div className="settings-import">
          <button className="back-btn" onClick={() => {
            setView('main');
            setImportCode('');
            setImportPassword('');
            setError(null);
            setSuccess(null);
          }}>
            ← Back
          </button>

          <h3>Import Account</h3>
          <p>Restore your account from a backup code.</p>

          <div className="import-form">
            <div className="form-group">
              <label>Backup Code</label>
              <textarea
                value={importCode}
                onChange={(e) => setImportCode(e.target.value)}
                placeholder="Paste your backup code here (starts with aos1:...)"
                rows={4}
                disabled={loading}
              />
              <div className="file-drop-hint">
                or{' '}
                <label className="file-label">
                  <input
                    type="file"
                    accept=".txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileImport(file);
                    }}
                    style={{ display: 'none' }}
                  />
                  <span>select a backup file</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={importPassword}
                onChange={(e) => setImportPassword(e.target.value)}
                placeholder="Enter your backup password"
                disabled={loading}
              />
            </div>

            <button
              className="restore-btn"
              onClick={handleImport}
              disabled={loading || !importCode.trim() || !importPassword}
            >
              {loading ? 'Restoring...' : 'Restore Account'}
            </button>

            <div className="import-warning">
              <strong>Warning:</strong> This will replace your current identity.
              You will lose access to any content created with your current account.
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="message error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {success && (
        <div className="message success">
          {success}
        </div>
      )}
    </div>
  );
}
